export const DEFAULT_THEME_COLOR = '#0f766e';

function clampChannel(value) {
  return Math.max(0, Math.min(255, Math.round(value)));
}

function parseHexColor(value) {
  const match = String(value || '').trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) return null;

  const hex = match[1];
  if (hex.length === 3) {
    return {
      r: parseInt(hex[0] + hex[0], 16),
      g: parseInt(hex[1] + hex[1], 16),
      b: parseInt(hex[2] + hex[2], 16),
    };
  }

  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  };
}

function parseCssColor(value) {
  const directHex = parseHexColor(value);
  if (directHex) return directHex;

  if (typeof document === 'undefined' || !document.body) return null;

  const probe = document.createElement('span');
  probe.style.color = String(value || '').trim();
  if (!probe.style.color) return null;
  probe.style.display = 'none';
  document.body.appendChild(probe);
  const computed = window.getComputedStyle(probe).color;
  document.body.removeChild(probe);
  const match = computed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (!match) return null;

  return {
    r: Number(match[1]),
    g: Number(match[2]),
    b: Number(match[3]),
  };
}

function rgbToHex({ r, g, b }) {
  return `#${[r, g, b].map((channel) => clampChannel(channel).toString(16).padStart(2, '0')).join('')}`;
}

function rgbToString({ r, g, b }) {
  return `${clampChannel(r)}, ${clampChannel(g)}, ${clampChannel(b)}`;
}

function mixRgb(base, target, weight) {
  return {
    r: clampChannel(base.r + (target.r - base.r) * weight),
    g: clampChannel(base.g + (target.g - base.g) * weight),
    b: clampChannel(base.b + (target.b - base.b) * weight),
  };
}

export function getThemePalette(value) {
  const base = parseCssColor(value) || parseCssColor(DEFAULT_THEME_COLOR) || { r: 15, g: 118, b: 110 };
  const primary = rgbToHex(base);
  const primaryDark = rgbToHex(mixRgb(base, { r: 17, g: 24, b: 39 }, 0.24));
  const accent = rgbToHex(mixRgb(base, { r: 255, g: 255, b: 255 }, 0.2));
  const primarySoft = rgbToHex(mixRgb(base, { r: 255, g: 255, b: 255 }, 0.88));

  return {
    primary,
    primaryDark,
    accent,
    primarySoft,
    primaryRgb: rgbToString(base),
  };
}

export function applyThemeToDocument(value) {
  if (typeof document === 'undefined') return getThemePalette(value);

  const palette = getThemePalette(value);
  const root = document.documentElement;
  root.style.setProperty('--primary', palette.primary);
  root.style.setProperty('--primary-dark', palette.primaryDark);
  root.style.setProperty('--accent', palette.accent);
  root.style.setProperty('--primary-rgb', palette.primaryRgb);
  root.style.setProperty('--primary-soft', palette.primarySoft);

  const themeColorMeta = document.querySelector('meta[name="theme-color"]');
  if (themeColorMeta) {
    themeColorMeta.setAttribute('content', palette.primary);
  }

  return palette;
}
